// Apercu HTML du mail de notification du BON DE COMMANDE au prestataire (template Brevo 12, envoye
// par le back a l'acceptation : BdCController::accept (panier normal) ET acceptAcheteur (panier
// inverse) -> meme template). Rendu par le composant partage MailPreview (iframe sandbox).
//
// Gabarit BC AUTONOME : le style email est calque sur celui du MS mais ce fichier n'importe RIEN du
// module MS (isolation MS/BC). Partage entre les deux vues BC (presta + acheteur), qui montrent le
// meme mail au meme candidat. Le texte reproduit VERBATIM le template Brevo 12.

export interface BonCommandeMailParams {
  // {{ params.operation_numero }}
  operationNumero: string;
  // {{ params.operation_created_at }} — date du bon de commande (JJ/MM/AAAA HH:mm)
  operationDate: string;
  // {{ params.operation_nom }}
  operationNom: string;
  // {{ params.operation_prix_total }} — montant total HT deja formate (sans le "€", ajoute par le gabarit)
  montant: string;
  // {{ params.operation_acceptation }} — date d'acceptation (rend le bon executoire)
  acceptationDate: string;
  // {{ params.interlocuteur_name }}
  interlocuteurName: string;
  // {{ params.operation_phone }}
  interlocuteurPhone: string;
  // Titre de l'apercu : "Envoyé le ..." si notifie, sinon "Aperçu ...".
  dateEnvoi?: string | null;
}

// Echappement HTML : les valeurs API (noms, interlocuteur...) sont interpolees dans une string HTML
// injectee dans une iframe. Sans echappement = surface XSS stockee.
const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// Styles du gabarit email (calques sur le rendu MS, sans dependance au module MS).
const MAIL_STYLES = `
  body {
    font-family: "Segoe UI", Roboto, Arial, sans-serif;
    background-color: #f7f9fb;
    padding: 40px;
    color: #333;
    line-height: 1.6;
  }
  .header-container {
    max-width: 700px;
    margin: 0 auto;
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    padding: 40px 50px;
  }
  .mail-container {
    max-width: 700px;
    margin: 20px auto 0 auto;
    background: #ffffff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
    padding: 40px 50px;
  }
  h1 { font-size: 20px; margin-bottom: 25px; border-bottom: 1px solid #eee; padding-bottom: 10px; }
  h2 { font-size: 16px; margin: 0; }
  p { margin: 14px 0; font-size: 15px; }
  strong { color: #000; }
  .cta {
    padding: 1rem 1.5rem;
    font-size: 1.1rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background-color: #f97316;
    color: #fffdfc;
    border: none;
    border-radius: 50px;
    font-weight: 700;
    text-decoration: none;
    margin: 1.5rem auto 0 auto;
  }
  .footer { margin-top: 40px; padding-top: 15px; border-top: 1px solid #eee; font-size: 14px; color: #666; }`;

export const buildBonCommandeMailPreview = (params: BonCommandeMailParams): string => {
  const numero = escapeHtml(String(params.operationNumero || 'XXX'));
  const date = escapeHtml(params.operationDate || 'Date inconnue');
  const nom = escapeHtml(params.operationNom || "Nom de l'opération");
  const montant = escapeHtml(params.montant || '0,00');
  const acceptation = escapeHtml(params.acceptationDate || 'Date inconnue');
  const interlocuteur = escapeHtml(params.interlocuteurName || 'INTERLOCUTEUR');
  const telephone = escapeHtml(params.interlocuteurPhone || '00 00 00 00 00');
  const titre = params.dateEnvoi
    ? `Envoyé le ${escapeHtml(params.dateEnvoi)}`
    : 'Aperçu du message de notification au prestataire';

  return `
<html>
  <head>
    <meta charset="utf-8" />
    <style>${MAIL_STYLES}</style>
  </head>
  <body>
    <div class="header-container">
      <h2>${titre}</h2>
    </div>
    <div class="mail-container">
      <h1>Bon de commande</h1>
      <p>Cher prestataire,</p>
      <p>
        Par le présent mail, nous vous notifions le Bon de commande N°${numero} du ${date} pour
        l’opération ${nom}, d’un montant de ${montant} €, et le rendons exécutoire à partir de votre
        acceptation, c’est-à-dire le ${acceptation}. L’interlocuteur identifié est
        <strong>${interlocuteur}</strong> dont le N° de tél est <strong>${telephone}</strong>.
        N’hésitez pas à le joindre. Vous pouvez retrouver l’intégralité du suivi concernant ce bon
        sur votre espace en ligne et vous rappelons qu’il vous appartient de maintenir à jour vos
        pièces telles qu’assurances, attestations, etc…
      </p>
      <span class="cta">Voir le Bon de commande</span>
      <div class="footer">
        <p>Cordialement,<br/><strong>L’équipe Centralis</strong></p>
      </div>
    </div>
  </body>
</html>
`;
};

// --- Expiration (template Brevo 13) -------------------------------------------------------------
// Mail envoye au PRESTATAIRE quand sa commande expire sans reponse — BdCController, table
// `$scenarios` : `'expired' => 13`, destinataire `$prestataireEmail`. A ne pas confondre avec les
// templates 6 (passage au rang suivant) et 7 (expiration finale), qui partent a l'ACHETEUR.
//
// Le back envoie aussi `operation_numero`, `total_commande`, `jours_restant`, `interlocuteur_nom` et
// `interlocuteur_phone` : le template ne les utilise pas. On ne les expose donc pas ici — cet apercu
// doit montrer ce que le prestataire LIT, pas ce que le back transmet.

export interface ExpirationMailParams {
  // {{ params.date }} — date d'expiration (JJ/MM/AAAA)
  date: string;
  // {{ params.operation_nom }}
  operationNom: string;
  // {{ params.organisme_nom }} — l'acheteur
  organismeNom: string;
  // {{ params.operation_prix_total }} — montant total deja formate (sans le "€", ajoute par le gabarit)
  montant: string;
  // Titre de l'apercu : "Envoyé le ..." si le mail est parti, sinon "Aperçu ...".
  dateEnvoi?: string | null;
}

export const buildExpirationMailPreview = (params: ExpirationMailParams): string => {
  const date = escapeHtml(params.date || 'Date inconnue');
  const nom = escapeHtml(params.operationNom || "Nom de l'opération");
  const organisme = escapeHtml(params.organismeNom || "l'acheteur");
  const montant = escapeHtml(params.montant || '0,00');
  const titre = params.dateEnvoi
    ? `Envoyé le ${escapeHtml(params.dateEnvoi)}`
    : "Aperçu du message d'expiration au prestataire";

  // Texte VERBATIM du template 13. "refus ou absence de reponse" est la formulation du template :
  // cote BC seule l'expiration declenche ce mail, mais on ne reecrit pas le libelle ici — l'apercu
  // mentirait sur ce que le prestataire recoit reellement.
  return `
<html>
  <head>
    <meta charset="utf-8" />
    <style>${MAIL_STYLES}</style>
  </head>
  <body>
    <div class="header-container">
      <h2>${titre}</h2>
    </div>
    <div class="mail-container">
      <h1>Bon de commande</h1>
      <p>Cher prestataire,</p>
      <p>
        Nous accusons réception en date du ${date} de votre refus ou de votre absence de réponse à
        l’opération ${nom} de la part de l’acheteur ${organisme} d'un montant de ${montant} €.
      </p>
      <p>
        Nous vous rappelons que les refus et absences de réponses, répétées et/ou non légitimes,
        nuisent à la qualité de notre centrale d’achat. Elles peuvent donc donner lieu à des
        déclassement ou des déréférencements.
      </p>
      <div class="footer">
        <p>Cordialement,<br/><strong>L’équipe Centralis</strong></p>
      </div>
    </div>
  </body>
</html>
`;
};
